import { textFormatting } from '@yola/ws-sdk';
import customUI from 'src/js/modules/custom-ui';
import constants from './constants';
import controls from './controls';
import helpers from './helpers';
import AiRewriteTextContainer from './containers/ai-rewrite-text-container';
import AiWriteTextContainer from './containers/ai-write-text-container';

const {
  linkControl,
  listBulletControl,
  listNumberControl,
  alignControl,
  clearControl,
  textColorControl,
  insertImageControl,
  fontSizeControl,
  aiWriteTextControl,
  aiRewriteTextControl,
  aiRewriteCollapsedTextControl,
  textFormattingControl,
  textIndentControl,
  textOutdentControl,
  lineAndParagraphSpacingControl,
  getElementTypeControl,
} = controls;
const { afterViewLoad, isSupportedColorOptions, removeDynamicAttributes } = helpers;
const { addRelatedClassesToElement, removeRelatedClassesFromElement } = textFormatting.helpers;

const getExtension = () => {
  const controlList = [
    textFormattingControl,
    linkControl,
    insertImageControl,
    listBulletControl,
    listNumberControl,
    alignControl,
    clearControl,
    getElementTypeControl(),
    fontSizeControl,
    aiWriteTextControl,
    aiRewriteTextControl,
    aiRewriteCollapsedTextControl,
    lineAndParagraphSpacingControl,
    textIndentControl,
    textOutdentControl,
  ];

  if (isSupportedColorOptions()) {
    controlList.splice(1, 0, textColorControl);
  }

  return {
    injections: {
      afterElementInsert: addRelatedClassesToElement,
      afterElementSynchronize: removeRelatedClassesFromElement,
      afterViewLoad,
    },
    hooks: {
      afterBlurFocusedElement: addRelatedClassesToElement,
      beforeBlurFocusedElement: removeRelatedClassesFromElement,
      beforeSynchronizeStaticElement: (oldElement, newElement) => {
        removeDynamicAttributes(newElement);
      },
    },
    slug: constants.common.SLUG,
    extensionSlug: constants.common.SLUG,
    title: 'Text Formatting Extension',
    description: 'Extension for editing text',
    settings: {
      textContainer: 'ws-text',

      textFormattingRestrictedParentsSelector:
        "*:not([type='gdpr-consent']) > ws-label, ws-button > a,[slot='footer-navigation'], [slot='site-name'], ws-nav, ws-counter, ws-contact-form-container",
      textFormattingQuerySelector: '*:not(ws-button):not(button):not(label)',

      textColorRestrictedParentsSelector:
        "*:not([type='gdpr-consent']) > ws-label, ws-button > a, [slot='footer-navigation'], [slot='site-name'], ws-nav, ws-counter, ws-contact-form-container, ws-cookie-banner, ws-header, .ws-m-schedule thead, [slot='product-tags'], ws-global:not([src*='/cta-text/'])",
      textColorQuerySelector: '*:not(ws-button):not(button):not(label)',

      linkAcceptedParentQuerySelector: 'ws-contact-form-container',
      linkQuerySelector: '*:not(ws-button):not(button):not(label)',
      linkRestrictedParentsSelector:
        "*:not([type='gdpr-consent']) > ws-label, [slot='footer-navigation'], [slot='site-name'], ws-nav, ws-counter",
      linkDeleteButtonQuerySelector: '*:not(ws-button) > a',

      bulletListQuerySelector:
        'ws-text p, ws-text h1, ws-text h2, ws-text h3, ws-text h4, ws-text h5, ws-text h6',
      bulletListRestrictedParentsSelector:
        ".ws-m-pricing-with-icon, .ws-m-pricing-table, .ws-m-table-with-buttons, [widget='follow-us']",

      numberedListQuerySelector:
        'ws-text p, ws-text h1, ws-text h2, ws-text h3, ws-text h4, ws-text h5, ws-text h6',
      numberedListRestrictedParentsSelector:
        ".ws-m-pricing-with-icon, .ws-m-pricing-table, .ws-m-table-with-buttons, [widget='follow-us']",

      clearRestrictedParentsSelector:
        "*:not([type='gdpr-consent']) > ws-label, [slot='footer-navigation'], [slot='site-name'], ws-nav, ws-counter, ws-contact-form-container, ws-button a",
      clearQuerySelector: '*:not(ws-button):not(button):not(label):not(.ws-link)',

      alignTextRestrictedParentsSelector:
        "ws-footer, ws-header,ws-cookie-banner, ws-text li, .ws-m-business-info, [id^='ws-block-business-card'], [id^='ws-block-about-with-features'] [slot='block-features'], [id^='ws-block-features-with-aside-icons-3-cols'] [slot='block-columns'], [id^='ws-block-features-with-counters'] [slot='block-columns'], [id^='ws-block-products-with-aside-media'] [slot='block-columns'], [id^='ws-block-testimonials-with-aside-media'] [slot='block-columns'], [id^='ws-block-cta-with-aside-button'], [id^='ws-block-pricing'] [slot='block-table'], [id^='ws-block-pricing'] [slot='block-columns'], [id^='ws-block-team-members-with-aside-media'] [slot='block-columns'], [id^='ws-block-contact'], [id^='ws-block-counters'] [slot='block-columns'], [id^='ws-block-schedule'] [slot='block-columns'], [widget='follow-us'], [widget='list-with-icons']",
      alignTextQuerySelector: '*',

      insertImageRestrictedParentsSelector: '',
      insertImageQuerySelector: "ws-block[src*='/text/'] ws-text",

      allowableQuoteMarkQuerySelector: "[id^='ws-block-article-'], [id^='ws-block-text']",

      elementTypeRestrictedParentsSelector: '',

      fontSizeRestrictedParentsSelector: '',

      textIndentQuerySelector: '*:not(.ws-m-blockquote-with-icon blockquote)',
      textIndentRestrictedParentsSelector: "[widget='follow-us']",

      aiTextAssistantRestrictedBlocksSelector: "ws-block[src*='/pricing-with-aside-cta/']",
      aiTextAssistantRewriteCollapsedTextRestrictedBlocksSelector:
        "ws-block[src*='/text/'][data-ws-variation-id*='article']",
    },
    ui: {
      control: controlList,
    },
    init() {
      customUI.operations.register(customUI.customUiTypes.AI_REWRITE, AiRewriteTextContainer, {
        controlShowingOnScroll: true,
      });
      customUI.operations.register(customUI.customUiTypes.AI_WRITE, AiWriteTextContainer, {
        controlShowingOnScroll: true,
      });
    },
  };
};

export default getExtension;
